import { SettingsMainGrid } from '@/components/settings/SettingsParts'
import {
  AdvancedSettingsSection,
  AutomationSection,
  ErpSection,
  FlagDifferencesSection,
  OrderSourceSection,
  ParseFormatSection,
  PriceThresholdsSection,
  QuantitySection,
  StockSection,
} from '@/features/sales-order/settings/SalesOrderSettingsSections'
import {
  SO_FLAG_FIELDS,
  applyAutomationToSettings,
  applyErpToSettings,
  applyFlaggedFieldsToSettings,
  applyOrderSourceToSettings,
  applyParseFormatToSettings,
  applyQuantityToSettings,
  applyStockToSettings,
  applyThresholdsToSettings,
  automationFromSettings,
  erpFromSettings,
  orderSourceFromSettings,
  flaggedFieldsFromSettings,
  parseFormatFromSettings,
  quantityFromSettings,
  stockFromSettings,
  thresholdsFromSettings,
  type ParseFormatSettings,
  type PriceThresholds,
  type SoFlagFieldId,
} from '@/features/sales-order/settings/salesOrderSettingsMappers'
import type { SalesOrderSettings } from '@/types/salesOrderSettings'

/**
 * Every sales order setting, in the order both pages show them.
 *
 * One component rather than the same sections written out on the workflow page and the customer
 * page: a customer's settings are the workflow's settings with different values, so a section
 * added to one and forgotten on the other would be a setting that could be changed in one place
 * and quietly overridden from the other.
 */
export function SalesOrderSettingsForm({
  settings,
  onChange,
}: {
  settings: SalesOrderSettings
  onChange: (next: SalesOrderSettings) => void
}) {
  function toggleFlaggedField(id: SoFlagFieldId, checked: boolean) {
    const flagged = flaggedFieldsFromSettings(settings)
    if (checked) flagged.add(id)
    else flagged.delete(id)
    onChange(applyFlaggedFieldsToSettings(settings, flagged))
  }

  function toggleParseFormat(key: keyof ParseFormatSettings, checked: boolean) {
    const parseFormat = parseFormatFromSettings(settings)
    parseFormat[key] = checked
    onChange(applyParseFormatToSettings(settings, parseFormat))
  }

  function changeThresholds(next: PriceThresholds) {
    onChange(applyThresholdsToSettings(settings, next))
  }

  return (
    <>
      <AutomationSection
        automation={automationFromSettings(settings)}
        onToggle={(checked) =>
          onChange(applyAutomationToSettings(settings, { fullyAutomaticValidation: checked }))
        }
      />
      {/* Left: what is checked on an order. Right: when it stops anyway, and what goes to the ERP
          and what the sales order is built from. */}
      <SettingsMainGrid
        wide={
          <div className="space-y-6">
            <FlagDifferencesSection
              flaggedFields={flaggedFieldsFromSettings(settings)}
              onToggle={toggleFlaggedField}
              onToggleAll={(on) =>
                onChange(applyFlaggedFieldsToSettings(settings, new Set(on ? SO_FLAG_FIELDS.map((field) => field.id) : [])))
              }
            />
            <StockSection
              stock={stockFromSettings(settings)}
              onToggle={(checked) =>
                onChange(applyStockToSettings(settings, { checkStockBalance: checked }))
              }
            />
          </div>
        }
        narrow={
          <>
            <PriceThresholdsSection
              thresholds={thresholdsFromSettings(settings)}
              onDraftChange={changeThresholds}
            />
            <ErpSection
              erp={erpFromSettings(settings)}
              onToggle={(checked) =>
                onChange(applyErpToSettings(settings, { uploadOrderDocumentToErp: checked }))
              }
            />
            <OrderSourceSection
              source={orderSourceFromSettings(settings)}
              onChange={(next) => onChange(applyOrderSourceToSettings(settings, next))}
            />
          </>
        }
      />
      <AdvancedSettingsSection>
        <ParseFormatSection parseFormat={parseFormatFromSettings(settings)} onToggle={toggleParseFormat} />
        <QuantitySection
          quantity={quantityFromSettings(settings)}
          onToggle={(checked) =>
            onChange(
              applyQuantityToSettings(settings, { multiplyQuantityByUnitsPerPackage: checked }),
            )
          }
        />
      </AdvancedSettingsSection>
    </>
  )
}
